// File: frontend/src/pages/public/HomePage.js

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchHomeFeed } from '../../services/api'; 
import { createExcerpt } from '../../utils/createExcerpt';
import { formatDate } from '../../utils/formatDate';
import MediaRenderer from '../../components/MediaRenderer';
import './HomePage.css'; 
import SkeletonLoader from '../../components/common/SkeletonLoader';
import { useLanguage } from '../../context/LanguageContext';
import adBannerImage from '../../assets/images/ad-banner.jpeg';
import { FaShareAlt } from 'react-icons/fa'; 
import WebStoriesWidget from '../../components/common/WebStoriesWidget';
import AdBanner from '../../components/common/AdBanner';

const HomePage = () => {
    const { language } = useLanguage();

    const seoData = {
        en: {
            title: "India Jagran: Latest News, Breaking Stories, Politics & Sports",
            description: "Read the latest news from India and the world. Get breaking news on Politics, Business, Bollywood, Sports, and more in English on India Jagran.",
            keywords: "India News, English News, Latest Updates, Breaking News, Politics, Sports, Business, India Jagran"
        },
        hi: {
            title: "India Jagran: देश और दुनिया की ताज़ा ख़बरें, ब्रेकिंग न्यूज़ और समाचार",
            description: "India Jagran पर पढ़िए देश और दुनिया की ताज़ा ख़बरें। राजनीति, व्यापार, बॉलीवुड, और खेल जगत की ब्रेकिंग न्यूज़ और सटीक विश्लेषण हिंदी में।",
            keywords: "India News, Hindi News, Samachar, Khabar, Breaking News, Politics, Sports, India Jagran, हिंदी समाचार"
        }
    };

    const currentSeo = seoData[language] || seoData['en'];

    const [allArticles, setAllArticles] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('National');

    // --- TRANSLATIONS ---
    const t = (key) => {
        const dict = {
            latestNews: { en: 'Latest News', hi: 'ताज़ा ख़बरें' },
            mustRead: { en: 'Must Read', hi: 'ज़रूर पढ़ें' },
            sports: { en: 'Sports', hi: 'खेल' },
            education: { en: 'Education', hi: 'शिक्षा' },
            health: { en: 'Health', hi: 'स्वास्थ्य' },
            tech: { en: 'Tech', hi: 'टेक' },
            crime: { en: 'Crime', hi: 'अपराध' },
            environment: { en: 'Environment', hi: 'पर्यावरण' },
            business: { en: 'Business', hi: 'बिज़नेस' },
            religion: { en: 'Religion', hi: 'धर्म' },
            viewAll: { en: 'View All', hi: 'सभी देखें' },
            moreStories: { en: 'More Stories', hi: 'अन्य कहानियाँ' }
        };
        return dict[key] ? dict[key][language] || dict[key]['en'] : key;
    };

    // --- DATA FETCHING ---
    useEffect(() => {
        const getArticles = async () => {
            setLoading(true);
            try {
                const response = await fetchHomeFeed(language);
                setAllArticles(response.data || []);
            } catch (error) {
                console.error("Failed to fetch articles:", error);
            } finally {
                setLoading(false);
            }
        };
        getArticles();
    }, [language]);

    // Helper: choose language-specific field
    const getLang = (article, field) => {
        if (!article) return '';
        const field_hi = `${field}_hi`;
        const field_en = `${field}_en`;
        const field_legacy = field;
        
        if (language === 'hi' && article[field_hi] && article[field_hi].trim() !== '') {
            return article[field_hi];
        }
        return article[field_en] || article[field_legacy] || ''; 
    };

    // --- STRICT FILTERING ---
    const publishedArticles = useMemo(() => {
        return allArticles.filter(a => a && a.status === 'published');
    }, [allArticles]);

    // --- LANGUAGE FILTERING ---
    const filteredArticles = useMemo(() => {
        return publishedArticles.filter(article => {
            const hasHiContent = (article.title_hi && article.title_hi.trim() !== '') ||
                                 (article.summary_hi && article.summary_hi.trim() !== '') ||
                                 (article.content_hi && article.content_hi.trim() !== '' && article.content_hi !== '<p></p>');

            const hasEnContent = (article.title_en && article.title_en.trim() !== '') ||
                                 (article.summary_en && article.summary_en.trim() !== '') ||
                                 (article.content_en && article.content_en.trim() !== '' && article.content_en !== '<p></p>');

            const hasLegacyTitle = article.title && !article.title_en && !article.title_hi;

            if (language === 'hi') {
                return hasHiContent;
            } else {
                return hasEnContent || hasLegacyTitle;
            }
        });
    }, [language, publishedArticles]);

    // --- SMART CATEGORY MAP ---
    const categoryKeywords = {
        'Business': ['business', 'vyapar', 'finance', 'money', 'market', 'बिज़नेस'],
        'Tech': ['tech', 'technology', 'science', 'mobile', 'gadget', 'ai', 'टेक', 'टेक्नोलॉजी', 'विज्ञान'],
        'Sports': ['sports', 'cricket', 'khel', 'game', 'खेल', 'क्रिकेट'],
        'National': ['national', 'india', 'rashtriya', 'bharat', 'uttar pradesh', 'delhi', 'maharashtra', 'राष्ट्रीय', 'भारत'],
        'World': ['world', 'international', 'duniya', 'global', 'asia', 'us', 'europe', 'विश्व', 'अंतर्राष्ट्रीय'],
        'Education': ['education', 'jobs', 'shiksha', 'career', 'exam', 'result', 'शिक्षा', 'नौकरी'],
        'Health': ['health', 'fitness', 'swasthya', 'wellness', 'disease', 'medicine', 'स्वास्थ्य'],
        'Environment': ['environment', 'nature', 'paryavaran', 'climate', 'weather', 'pollution', 'पर्यावरण'],
        'Crime': ['crime', 'jurm', 'apradh', 'police', 'court', 'arrest', 'क्राइम', 'अपराध'],
        'Religion': ['religion', 'dharm', 'astha', 'temple', 'pooja', 'bhakti', 'धर्म', 'आस्था', 'पूजा', 'mandir']
    };

    // --- UPDATED MATCHING LOGIC ---
    const articleMatchesCategory = (article, categoryName) => {
        if (!article || !categoryName) return false;
        const keywords = (categoryKeywords[categoryName] || [categoryName.toLowerCase()]);
        
        const catVal = (article.category || '').toString().toLowerCase();
        
        // 🔥 FIX 1: 'article.subCategory' ko 'article.subcategory' (lowercase) kar diya
        // Backend se 'subcategory' aata hai, 'subCategory' nahi.
        const subVal = (article.subcategory || article.subCategory || '').toString().toLowerCase();
        
        const tags = Array.isArray(article.tags) ? article.tags.map(t => t.toLowerCase()) : [];

        // --- STRICT BLOCKING LOGIC ---
        if (categoryName === 'Tech') {
            if (catVal.includes('tech') || catVal.includes('science') || catVal.includes('विज्ञान')) return true;
            
            const strictBlockList = [
                'national', 'world', 'politics', 'opinion', 'sports', 'cricket', 'entertainment',
                'business', 'health', 'crime', 'education', 'lifestyle', 'editorial',
                'राष्ट्रीय', 'विश्व', 'राजनीति', 'खेल', 'मनोरंजन', 'बिज़नेस', 'स्वास्थ्य', 'religion', 'dharm'
            ];
            
            // Ab ye logic sahi kaam karega kyunki 'subVal' mein sahi data aa raha hai
            if (strictBlockList.some(block => catVal.includes(block))) {
                if (subVal.includes('tech') || subVal.includes('gadget') || subVal.includes('mobile')) return true;
                return false;
            }
        }

        if (categoryName === 'Environment') {
            const excludedCats = ['business', 'tech', 'entertainment', 'sports', 'national', 'politics'];
            if (excludedCats.some(ex => catVal.includes(ex))) return false;
        }
        
        if (catVal && keywords.some(k => catVal.includes(k))) return true;
        if (subVal && keywords.some(k => subVal.includes(k))) return true;
        if (tags.length > 0 && tags.some(tag => keywords.some(k => tag.includes(k)))) return true;
        const title = (article.title || article.title_en || article.title_hi || '').toString().toLowerCase();
        if (title && keywords.some(k => title.includes(k))) return true;
        
        return false;
    };

    const getSectionData = (categoryName, count = 6) => {
        const data = filteredArticles.filter(a => articleMatchesCategory(a, categoryName));
        return data.slice(0, count);
    };

    const handleShare = (e, article) => {
        e.preventDefault(); e.stopPropagation();
        if (!article) return;
        const shareUrl = `${window.location.origin}/article/${article.slug}`;
        const shareTitle = article.shortHeadline || getLang(article, 'title');
        if (navigator.share) { navigator.share({ title: shareTitle, text: 'Check out this news!', url: shareUrl }); }
        else { navigator.clipboard.writeText(shareUrl); alert(language === 'hi' ? 'लिंक कॉपी हो गया!' : 'Link copied!'); }
    };

    const renderSimpleListItem = (article) => {
        if (!article) return null;
        const title = article.shortHeadline || getLang(article, 'title');
        return (
            <li key={article._id} className="list-item-with-share">
                <Link to={`/article/${article.slug}`} className="list-link-text">{title}</Link>
                <button className="list-share-btn" onClick={(e) => handleShare(e, article)}><FaShareAlt /></button>
            </li>
        );
    };

    // 🔥 FIX 2: List Rendering Logic (Layout Fix + Data Fix)
 const renderSideListWithThumbnail = (article) => {

        if (!article) return null;

        const title = article.shortHeadline || getLang(article, 'title');

        return (

             <div key={article._id} className="latest-news-item" style={{position: 'relative'}}>

                <button className="side-share-btn" onClick={(e) => handleShare(e, article)}>

                    <FaShareAlt />

                </button>

                <Link to={`/article/${article.slug}`}>

                    <div className="list-item-media">

                        <MediaRenderer filePath={article.featuredImage} altText={title} />

                    </div>

                    <div className="list-item-content">

                        <p>{title}</p>

                        <span className="timestamp">{formatDate(article.createdAt)}</span>

                    </div>

                </Link>

            </div>

        )

    }

    const renderCard = (article) => {
        if (!article) return null;
        const title = article.shortHeadline || getLang(article, 'title');
        const summary = getLang(article, 'summary') || getLang(article, 'content');
        return (
            <div key={article._id} className="story-card">
                <button className="card-share-button" onClick={(e) => handleShare(e, article)}><FaShareAlt /></button>
                <Link to={`/article/${article.slug}`}>
                    <div className="card-media"><MediaRenderer filePath={article.featuredImage} altText={title} /></div>
                    <div className="card-content">
                        {/* 🔥 FIX 3: Display Tag - Using 'subcategory' lowercase */}
                        <span className="category-tag">
                            {article.category}
                            {article.subcategory ? ` • ${article.subcategory}` : ''}
                        </span>

                        <h2>{title}</h2> 
                        <p className="card-excerpt">{createExcerpt(summary, 15)}</p>
                        <span className="card-date">{formatDate(article.createdAt)}</span>
                    </div>
                </Link>
            </div>
        );
    };

    const SectionTitle = ({ titleKey, categorySlug }) => (
        <div className="section-header">
            <h2 className="category-section-title">{t(titleKey)}</h2>
            {categorySlug && <Link to={`/category/${categorySlug}`} className="view-all-link">{t('viewAll')} &rarr;</Link>}
        </div>
    );

    if (loading) return <SkeletonLoader />;

    // --- DATA ALLOCATION ---
    const topStory = filteredArticles[0] || null;
    const latestNews = filteredArticles.slice(1, 6);     
    const mustReadStories = filteredArticles.slice(6, 11); 
    const moreStories = filteredArticles.slice(11, 23); 

    const nationalArticles = filteredArticles.filter(a => articleMatchesCategory(a, 'National')).slice(0, 12);
    const internationalArticles = filteredArticles.filter(a => articleMatchesCategory(a, 'World')).slice(0, 12);

    const sportsData = getSectionData('Sports', 4);
    const eduData = getSectionData('Education', 4);
    const healthData = getSectionData('Health', 4);
    const crimeData = getSectionData('Crime', 4); 
    const envData = getSectionData('Environment', 4);
    const bizData = getSectionData('Business', 4);
    
    // 🔥 Tech Data wapis le aaye hain
    const techData = getSectionData('Tech', 5); 
    const religionData = getSectionData('Religion', 5); 

    const isHindi = language === 'hi';
    const leftSectionData = isHindi ? religionData : techData;
    const leftSectionTitleKey = isHindi ? 'religion' : 'tech';
    const leftSectionSlug = isHindi ? 'religion' : 'tech';

    return (
        <div className="container">
        
        {/* --- 2. DYNAMIC HELMET SETUP --- */}
            <Helmet>
                {/* यह HTML tag में lang="hi" या lang="en" अपने आप सेट करेगा */}
                <html lang={language} /> 
                
                <title>{currentSeo.title}</title>
                <meta name="description" content={currentSeo.description} />
                <meta name="keywords" content={currentSeo.keywords} />
                
                {/* Canonical URL same rahega */}
                <link rel="canonical" href="https://www.indiajagran.com/" />
                
                {/* Social Media Tags bhi Dynamic karein */}
                <meta property="og:title" content={currentSeo.title} />
                <meta property="og:description" content={currentSeo.description} />
                <meta property="og:locale" content={language === 'hi' ? 'hi_IN' : 'en_IN'} />
            </Helmet>

            {/* ✅ AD LOCATION 1: TOP LEADERBOARD (High Revenue) */}
            <div style={{ margin: '20px 0', minHeight: '90px', textAlign: 'center' }}>
                <AdBanner dataAdSlot="8328096821" /> 
            </div>

            {/* --- TOP GRID --- */}
            <div className="home-grid-container">
                <aside className="left-column">
                    <h2 className="section-title">{t('latestNews')}</h2>
                    <div className="latest-news-list">
                        {latestNews.map(article => renderSideListWithThumbnail(article))}
                    </div>

                    <div style={{ marginBottom: '20px', minHeight: '250px' }}>
                        <AdBanner dataAdSlot="8328096821" />
                    </div>
                    
                </aside>

                <main className="center-column">
                    {topStory ? (
                        <div style={{position: 'relative'}}>
                            <button className="main-share-btn" onClick={(e) => handleShare(e, topStory)}>
                                <FaShareAlt />
                            </button>
                            <Link to={`/article/${topStory.slug}`}>
                                <div className="big-story-content" style={{ marginBottom: '15px' }}>
                                    {topStory.kicker && <span className="big-story-kicker">{topStory.kicker}</span>}
                                    <h1>{topStory.longHeadline || getLang(topStory, 'title')}</h1>
                                </div>
                                <div className="big-story-media" style={{ marginBottom: '15px', aspectRatio: '16/9', overflow: 'hidden', borderRadius: '8px' }}>
                                    <MediaRenderer filePath={topStory.featuredImage} altText={topStory.longHeadline || getLang(topStory, 'title')} />
                                </div>
                                {topStory.thumbnailCaption && (
                                    <p className="homepage-thumbnail-caption" style={{ marginBottom: '15px', color: '#666', fontSize: '0.95rem' }}>
                                        {topStory.thumbnailCaption}
                                    </p>
                                )}
                                <div className="big-story-bottom-summary" style={{ marginTop: '10px' }}>
                                    <p className="big-story-excerpt" style={{ lineHeight: '1.6' }}>
                                        {createExcerpt(getLang(topStory, 'summary') || getLang(topStory, 'content'), 150)}
                                    </p>
                                </div>
                            </Link>
                        </div>
                    ) : (
                        <div className="no-news-placeholder"><p>No News Available for this Language.</p></div>
                    )}
                </main>

                <aside className="right-column">
                    {/* ✅ AD LOCATION 2: SIDEBAR (Square Ad) */}
                    <div style={{ marginBottom: '20px', minHeight: '250px' }}>
                        <AdBanner dataAdSlot="8328096821" />
                    </div>

                    <h2 className="section-title">{t('mustRead')}</h2>
                    <div className="latest-news-list"> 
                        {mustReadStories.map(article => renderSideListWithThumbnail(article))}
                    </div>
                </aside>
            </div>

            {/* ✅ UPDATED WEB STORIES SECTION (Replace the old div with this) */}
            <div className="container" style={{ marginTop: '30px', marginBottom: '30px' }}>
                <WebStoriesWidget />
            </div>

            {/* DUAL ADS */}
            <div className="dual-ad-section">
                <div className="ad-card">
                    <a href="https://wa.me/919792117190" target="_blank" rel="noopener noreferrer" className="ad-link">
                        <div className="ad-image-side"><img src={adBannerImage} alt="AutoZone" /></div>
                        <div className="ad-content-side">
                            <span className="ad-tag">Sponsored</span>
                            <h3 className="ad-heading">AutoZone - Genuine Parts</h3>
                            <p className="ad-info">Bumper, Grill & Accessories at Wholesale Rates.<br /><b>Address:</b> Near Nanak Chauki, Gola Gaokaran Nath, Lakhimpur<br /> kheri, 262802</p>
                            <span className="ad-btn">Contact Now &rarr;</span>
                        </div>
                    </a>
                </div>
                <div className="ad-card">
                    <a href="https://wa.me/919792117190" target="_blank" rel="noopener noreferrer" className="ad-link">
                        <div className="ad-image-side"><img src={adBannerImage} alt="AutoZone" /></div>
                        <div className="ad-content-side">
                            <span className="ad-tag">Offer</span>
                            <h3 className="ad-heading">Car Body Parts</h3>
                            <p className="ad-info">Get heavy discounts on JPT parts. Visit Gola.<br /><b>Address:</b> Near Nanak Chauki, Gola Gaokaran Nath, Lakhimpur<br /> kheri, 262802</p>
                            <span className="ad-btn">WhatsApp &rarr;</span>
                        </div>
                    </a>
                </div>
            </div>

            {/* ✅ AD LOCATION 3: MIDDLE HORIZONTAL AD */}
            <div style={{ margin: '30px 0', minHeight: '100px' }}>
                 <AdBanner dataAdSlot="7132389059" /> {/* Use In-Feed Ad ID here */}
            </div>

            <section className="category-section">
                <h2 className="section-title">{t('moreStories')}</h2>
                <div className="category-grid layout-B" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '18px' }}>
                    {moreStories.map(article => renderCard(article))}
                </div>
            </section>

            <section className="category-section tab-section" style={{ marginTop: '28px' }}>
                <div className="tabs-container">
                    <div className="tab-buttons">
                        <button className={`tab-button ${activeTab === 'National' ? 'active' : ''}`} onClick={() => setActiveTab('National')}>{language === 'hi' ? 'राष्ट्रीय' : 'National'}</button>
                        <span className="tab-separator">|</span>
                        <button className={`tab-button ${activeTab === 'International' ? 'active' : ''}`} onClick={() => setActiveTab('International')}>{language === 'hi' ? 'अंतर्राष्ट्रीय' : 'International'}</button>
                    </div>
                    <Link to={`/category/${activeTab.toLowerCase()}`} className="view-all-link">{t('viewAll')} &rarr;</Link>
                </div>
                <div className="category-grid layout-C" style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '18px' }}>
                    {(activeTab === 'National' ? nationalArticles : internationalArticles).map(article => renderCard(article))}
                </div>
            </section>

            <div className="multi-section-row layout-3-col" style={{ marginTop: '28px' }}>
                {sportsData.length > 0 && (
                    <section className="category-sub-section">
                        <SectionTitle titleKey="sports" categorySlug="sports" />
                        <div className="sub-section-content" style={{position: 'relative'}}>
                            <button className="overlay-share-btn" onClick={(e) => handleShare(e, sportsData[0])} style={{position:'absolute', top:'5px', right:'5px'}}><FaShareAlt /></button>
                            <Link to={`/article/${sportsData[0].slug}`} className="large-story-link">
                                <div className="large-story-media"><MediaRenderer filePath={sportsData[0].featuredImage} /></div>
                                <h4>{getLang(sportsData[0], 'title')}</h4>
                            </Link>
                            <ul className="small-story-list">{sportsData.slice(1, 3).map(article => renderSimpleListItem(article))}</ul>
                        </div>
                    </section>
                )}
                {eduData.length > 0 && (
                    <section className="category-sub-section">
                        <SectionTitle titleKey="education" categorySlug="education" />
                        <div className="sub-section-content" style={{position: 'relative'}}>
                            <button className="overlay-share-btn" onClick={(e) => handleShare(e, eduData[0])} style={{position:'absolute', top:'5px', right:'5px'}}><FaShareAlt /></button>
                            <Link to={`/article/${eduData[0].slug}`} className="large-story-link">
                                <div className="large-story-media"><MediaRenderer filePath={eduData[0].featuredImage} /></div>
                                <h4>{getLang(eduData[0], 'title')}</h4>
                            </Link>
                            <ul className="small-story-list">{eduData.slice(1, 3).map(article => renderSimpleListItem(article))}</ul>
                        </div>
                    </section>
                )}
                {healthData.length > 0 && (
                    <section className="category-sub-section">
                        <SectionTitle titleKey="health" categorySlug="health" />
                        <div className="sub-section-content" style={{position: 'relative'}}>
                            <button className="overlay-share-btn" onClick={(e) => handleShare(e, healthData[0])} style={{position:'absolute', top:'5px', right:'5px'}}><FaShareAlt /></button>
                            <Link to={`/article/${healthData[0].slug}`} className="large-story-link">
                                <div className="large-story-media"><MediaRenderer filePath={healthData[0].featuredImage} /></div>
                                <h4>{getLang(healthData[0], 'title')}</h4>
                            </Link>
                            <ul className="small-story-list">{healthData.slice(1, 3).map(article => renderSimpleListItem(article))}</ul>
                        </div>
                    </section>
                )}
            </div>

            {/* --- TECH (Using Tech Layout) & CRIME ROW --- */}
            <div className="tech-crime-row" style={{ display: 'flex', gap: '20px', margin: '30px 0', flexWrap: 'wrap' }}>
                <section className="tech-section science-tech-layout" style={{ flex: '2', minWidth: '300px' }}>
                    <div className="section-header"> 
                        <h2 className="category-section-title">{t(leftSectionTitleKey)}</h2>
                        <Link to={`/category/${leftSectionSlug}`} className="view-all-link">{t('viewAll')} &rarr;</Link>
                    </div>
                    
                    {leftSectionData.length > 0 && (
                        /* 🔥 LAYOUT FIXED: Flexbox Forced for Left-Right Layout */
                        <div className="tech-grid-main-content" style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', marginTop: '15px' }}> 
                            
                            {/* --- LEFT SIDE: BIG STORY (60% Width) --- */}
                            <div className="tech-main-story" style={{ flex: '6', position: 'relative' }}>
                                <button className="overlay-share-btn" onClick={(e) => handleShare(e, leftSectionData[0])} style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 2 }}><FaShareAlt /></button>
                                <Link to={`/article/${leftSectionData[0].slug}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                                    <div className="tech-main-media" style={{ width: '100%', aspectRatio: '16/9', overflow: 'hidden', borderRadius: '8px', marginBottom: '10px' }}>
                                        <MediaRenderer filePath={leftSectionData[0].featuredImage} />
                                    </div>
                                    <h3 className="tech-main-title" style={{ fontSize: '1.2rem', fontWeight: 'bold', margin: '0 0 8px 0', lineHeight: '1.4' }}>
                                        {getLang(leftSectionData[0], 'title')}
                                    </h3>
                                </Link>
                                <p className="tech-main-date" style={{ color: '#666', fontSize: '0.85rem' }}>{formatDate(leftSectionData[0].createdAt)}</p>
                            </div>

                            {/* --- RIGHT SIDE: LIST (40% Width) --- */}
                            <div className="tech-side-list" style={{ flex: '4', display: 'flex', flexDirection: 'column', gap: '15px' }}> 
                                {leftSectionData.slice(1, 5).map(article => renderSideListWithThumbnail(article))}
                            </div>
                        </div>
                    )}
                </section>

                <section className="crime-section" style={{ flex: '1', minWidth: '250px' }}>
                    <SectionTitle titleKey="crime" categorySlug="crime" />
                    {crimeData.length > 0 && (
                         <div className="sub-section-content">
                             <div style={{position:'relative'}}>
                                <button className="overlay-share-btn" onClick={(e) => handleShare(e, crimeData[0])} style={{position:'absolute', top:'5px', right:'5px'}}><FaShareAlt /></button>
                                <Link to={`/article/${crimeData[0].slug}`} className="large-story-link">
                                    <div className="large-story-media" style={{marginBottom:'10px'}}><MediaRenderer filePath={crimeData[0].featuredImage} /></div>
                                    <h4 style={{fontSize:'1.1rem'}}>{getLang(crimeData[0], 'title')}</h4>
                                </Link>
                            </div>
                            <ul className="small-story-list">{crimeData.slice(1, 4).map(a => renderSimpleListItem(a))}</ul>
                         </div>
                    )}
                </section>
            </div>

            <div className="multi-section-row layout-2-col-wide">
                 {envData.length > 0 && (
                    <section className="category-sub-section">
                        <SectionTitle titleKey="environment" categorySlug="environment" />
                         <div className="sub-section-content" style={{position: 'relative'}}>
                            <button className="main-share-btn small" onClick={(e) => handleShare(e, envData[0])} style={{top: '5px', right: '5px'}}><FaShareAlt /></button>
                            <Link to={`/article/${envData[0].slug}`} className="large-story-link">
                                <div className="large-story-media"><MediaRenderer filePath={envData[0].featuredImage} altText={envData[0].shortHeadline} /></div>
                                <h4>{getLang(envData[0], 'title')}</h4>
                            </Link>
                            <ul className="small-story-list">{envData.slice(1, 3).map(article => renderSimpleListItem(article))}</ul>
                        </div>
                    </section>
                 )}
                 {bizData.length > 0 && (
                    <section className="category-sub-section">
                        <SectionTitle titleKey="business" categorySlug="business" />
                         <div className="sub-section-content" style={{position: 'relative'}}>
                            <button className="main-share-btn small" onClick={(e) => handleShare(e, bizData[0])} style={{top: '5px', right: '5px'}}><FaShareAlt /></button>
                            <Link to={`/article/${bizData[0].slug}`} className="large-story-link">
                                <div className="large-story-media"><MediaRenderer filePath={bizData[0].featuredImage} altText={bizData[0].shortHeadline} /></div>
                                <h4>{getLang(bizData[0], 'title')}</h4>
                            </Link>
                            <ul className="small-story-list">{bizData.slice(1, 3).map(article => renderSimpleListItem(article))}</ul>
                        </div>
                    </section>
                 )}
            </div>
        </div>
    );
};

export default HomePage;